/**
 * MfaEnforcementGate — renders the org-wide MFA enforcement UX based on the
 * server-computed `user.mfa_gate` (never trusts client state alone; the backend
 * also refuses every non-exempt endpoint once blocked).
 *
 *   - blocked  → full-screen, non-dismissable enrolment overlay (forced setup)
 *   - grace    → dismissable top banner with days remaining
 */
import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import MfaPanel from "@/components/settings/MfaPanel";
import { ShieldWarning, X } from "@phosphor-icons/react";

export default function MfaEnforcementGate() {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const gate = user && typeof user === "object" ? user.mfa_gate : null;
  if (!gate || !gate.required || gate.enrolled) return null;

  // Grace expired → forced full-screen enrolment.
  if (gate.blocked) {
    return (
      <div
        className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
        data-testid="mfa-forced-overlay"
      >
        <div className="max-w-2xl mx-auto mt-6 space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-red-300 bg-red-50">
            <ShieldWarning size={28} weight="bold" className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <h1 className="text-lg font-black text-red-800">MFA Wajib Diaktifkan</h1>
              <p className="text-sm text-red-700 mt-1">
                Masa tenggang telah berakhir. Anda harus mengaktifkan Two-Factor
                Authentication sekarang untuk bisa melanjutkan menggunakan aplikasi.
              </p>
            </div>
          </div>
          <MfaPanel onEnrolled={refreshUser} />
        </div>
      </div>
    );
  }

  // Within grace → nag banner.
  if (dismissed) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-[90] bg-amber-500 text-amber-950 px-4 py-2.5 flex items-center gap-3 shadow-md"
      data-testid="mfa-grace-banner"
    >
      <ShieldWarning size={20} weight="bold" className="shrink-0" />
      <p className="text-sm font-semibold flex-1 min-w-0">
        Aktifkan MFA dalam {gate.days_remaining} hari lagi.{" "}
        <a href="/settings" className="underline font-bold" data-testid="mfa-banner-setup-link">
          Setup sekarang
        </a>
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 p-1 hover:bg-amber-600/30 rounded"
        aria-label="Tutup"
        data-testid="mfa-banner-dismiss"
      >
        <X size={18} weight="bold" />
      </button>
    </div>
  );
}
